import { detectLocaleFromPath, type Locale } from '../i18n';
import { ROUTE_SLUGS } from '../i18n/routes';
import { ALL_LOCALES } from '../i18n/locales';
import { TECHNIQUES } from '@sudoku/shared';
import type { DeviceCategory, SourceCategory, AnalyticsTrackOptions } from '@sudoku/shared';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const CLIENT_ID_KEY = 'sudoku_analytics_client_id';
const SESSION_STORAGE_KEY = 'sudoku_analytics_session_v2';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || undefined;
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export type AnalyticsParamValue = string | number | boolean | null | undefined;
export type AnalyticsParams = Record<string, AnalyticsParamValue>;

export interface WebSessionState {
  session_id: string;
  last_user_interaction_at: number;
  landing_path: string;
  landing_locale: string;
  source_category: SourceCategory;
  device_category: DeviceCategory;
}

let clientIdPromise: Promise<string> | null = null;
let lastPageView: string | null = null;
let inMemorySession: WebSessionState | null = null;

const DISALLOWED_KEYS = new Set([
  'user_id', 'email', 'token', 'password', 'image', 'board', 'authorization',
]);

const SEARCH_DOMAINS = [
  'google.com', 'bing.com', 'yandex.com', 'yandex.ru',
  'duckduckgo.com', 'yahoo.com', 'ecosia.org', 'baidu.com',
];

const SOCIAL_DOMAINS = [
  'twitter.com', 'x.com', 't.co', 'facebook.com', 'instagram.com',
  'reddit.com', 'linkedin.com', 'pinterest.com', 'tiktok.com', 'youtube.com',
];

const KNOWN_CAMPAIGN_SOURCES = new Set([
  'google', 'bing', 'yandex', 'meta', 'facebook', 'instagram',
  'twitter', 'x', 'reddit', 'newsletter', 'app_launch', 'promo', 'partner',
]);

// Set of all valid technique slugs from the techniques catalog
const VALID_TECHNIQUE_SLUGS = new Set(TECHNIQUES.map((t) => t.id));

export function createId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to non-cryptographic browser fallback
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export function createEventId(): string {
  return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function createAttemptId(): string {
  return `att_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

async function getClientId(): Promise<string> {
  if (clientIdPromise) return clientIdPromise;
  clientIdPromise = Promise.resolve().then(() => {
    try {
      const existing = localStorage.getItem(CLIENT_ID_KEY);
      if (existing) return existing;
      const generated = createId();
      localStorage.setItem(CLIENT_ID_KEY, generated);
      return generated;
    } catch {
      // Private browsing / storage-disabled mode: keep in memory
      return createId();
    }
  });
  return clientIdPromise;
}

function cleanParams(params: AnalyticsParams): Record<string, string | number | boolean | null> {
  const result: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(params)) {
    if (Object.keys(result).length >= 20) break;
    if (value === undefined) continue;
    const lowerKey = key.toLowerCase();
    if (DISALLOWED_KEYS.has(lowerKey)) continue;

    if (typeof value === 'string') {
      const lowerVal = value.toLowerCase();
      // Drop sensitive email, token, URL or oversized values silently
      if (
        value.length > 160
        || value.includes('@')
        || lowerVal.includes('token=')
        || lowerVal.includes('bearer ')
        || lowerVal.startsWith('http://')
        || lowerVal.startsWith('https://')
      ) {
        continue;
      }
      result[key] = value;
    } else if (typeof value === 'number') {
      if (Number.isFinite(value)) result[key] = value;
    } else if (typeof value === 'boolean' || value === null) {
      result[key] = value;
    }
  }
  return result;
}

function isDomainMatch(hostname: string, targetDomain: string): boolean {
  return hostname === targetDomain || hostname.endsWith('.' + targetDomain);
}

export function resolveDeviceCategory(): DeviceCategory {
  try {
    if (typeof window === 'undefined') return 'unknown';
    const width = window.innerWidth;
    if (width < 768) return 'mobile_web';
    if (width >= 768 && width < 1024) return 'tablet';
    if (width >= 1024) {
      const isCoarseOnly = window.matchMedia && window.matchMedia('(pointer: coarse) and not (pointer: fine)').matches;
      if (isCoarseOnly) return 'tablet';
      return 'desktop';
    }
  } catch {
    // Fallback
  }
  return 'unknown';
}

export function resolveSourceCategory(): SourceCategory {
  try {
    if (typeof window === 'undefined') return 'unknown';

    // 1. Evaluate campaign query signals FIRST (before referrer)
    const search = window.location.search;
    if (search) {
      const params = new URLSearchParams(search);
      const hasUtm = (
        params.has('utm_source') ||
        params.has('utm_medium') ||
        params.has('utm_campaign') ||
        params.has('utm_term') ||
        params.has('utm_content') ||
        params.has('gclid') ||
        params.has('fbclid')
      );

      if (hasUtm) {
        const utmSource = params.get('utm_source')?.toLowerCase() || '';
        const utmMedium = params.get('utm_medium')?.toLowerCase() || '';

        const isRecognizedMedium = Boolean(
          utmMedium && (
            KNOWN_CAMPAIGN_SOURCES.has(utmMedium) ||
            utmMedium === 'cpc' || utmMedium === 'ppc' || utmMedium === 'cpm' || utmMedium === 'paid' ||
            utmMedium === 'banner' || utmMedium === 'paid_social' || utmMedium === 'email' || utmMedium === 'newsletter'
          )
        );
        const isRecognizedSource = Boolean(utmSource && KNOWN_CAMPAIGN_SOURCES.has(utmSource));

        if (isRecognizedMedium || isRecognizedSource) {
          return 'campaign';
        }
        // If UTM parameters exist but are unrecognized / suspicious (e.g. unknown utm_campaign) -> 'unknown' (NOT direct)
        return 'unknown';
      }
    }

    // 2. Evaluate document.referrer if present
    const referrer = document.referrer ? document.referrer.trim() : '';
    if (referrer) {
      let refUrl: URL;
      try {
        refUrl = new URL(referrer);
      } catch {
        return 'unknown';
      }

      const refHost = refUrl.hostname.toLowerCase();
      const currentHost = window.location.hostname.toLowerCase();

      // Internal referrer from same host is treated as direct / no external attribution
      if (refHost === currentHost) {
        return 'direct';
      }

      for (const domain of SEARCH_DOMAINS) {
        if (isDomainMatch(refHost, domain)) return 'organic_search';
      }

      for (const domain of SOCIAL_DOMAINS) {
        if (isDomainMatch(refHost, domain)) return 'social';
      }

      return 'referral';
    }

    // 3. Neither campaign signals nor external referrer -> true direct
    return 'direct';
  } catch {
    return 'unknown';
  }
}

/**
 * Normalizes the current pathname to a controlled route template or '/unknown'.
 * Never returns free-form path parameters, queries, or user IDs.
 */
export function normalizeRoutePath(rawPath: string): string {
  try {
    if (!rawPath || typeof rawPath !== 'string') return '/unknown';
    const cleanPath = rawPath.split('#')[0].split('?')[0].trim();
    if (!cleanPath.startsWith('/')) return '/unknown';
    if (cleanPath.startsWith('/admin')) return '/unknown';

    // 1. Direct match with static localized routes
    for (const loc of ALL_LOCALES) {
      const slugs = ROUTE_SLUGS[loc];
      for (const key of Object.keys(slugs) as (keyof typeof slugs)[]) {
        const slug = slugs[key];
        if (cleanPath === slug || (slug !== '/' && cleanPath === `${slug}/`)) {
          return slug;
        }
      }
    }

    // 2. Check technique education pages: /[locale]/[education-slug]/[technique-slug]
    for (const loc of ALL_LOCALES) {
      const eduBase = ROUTE_SLUGS[loc].education;
      const prefix = `${eduBase}/`;
      if (cleanPath.startsWith(prefix)) {
        const candidateSlug = cleanPath.slice(prefix.length).replace(/\/$/, '').toLowerCase();
        if (VALID_TECHNIQUE_SLUGS.has(candidateSlug)) {
          return `${eduBase}/${candidateSlug}`;
        }
      }
    }

    // 3. Known parameterized routes (normalized to template)
    if (cleanPath.startsWith('/room/')) return '/room/:id';
    if (cleanPath.startsWith('/user/')) return '/user/:id';
  } catch {
    // Fallback
  }
  return '/unknown';
}

const VALID_LOCALES = new Set(['tr', 'en', 'nl', 'de', 'fr', 'it', 'pl', 'ru', 'es']);
const VALID_SOURCES = new Set(['direct', 'organic_search', 'social', 'referral', 'campaign', 'unknown']);
const VALID_WEB_DEVICES = new Set(['desktop', 'mobile_web', 'tablet']);

function isValidSessionState(state: any): state is WebSessionState {
  if (!state || typeof state !== 'object') return false;
  if (typeof state.session_id !== 'string' || !/^[A-Za-z0-9_-]{8,128}$/.test(state.session_id)) return false;
  if (typeof state.last_user_interaction_at !== 'number' || !Number.isFinite(state.last_user_interaction_at)) return false;
  if (typeof state.landing_locale !== 'string' || !VALID_LOCALES.has(state.landing_locale)) return false;
  if (typeof state.source_category !== 'string' || !VALID_SOURCES.has(state.source_category)) return false;
  if (typeof state.device_category !== 'string' || !VALID_WEB_DEVICES.has(state.device_category)) return false;
  if (typeof state.landing_path !== 'string') return false;
  const normalized = normalizeRoutePath(state.landing_path);
  if (normalized === '/unknown' && state.landing_path !== '/unknown') return false;
  return true;
}

function getSessionFromStorage(): WebSessionState | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isValidSessionState(parsed)) {
      return parsed;
    }
    // Corrupted or invalid stored context: remove to regenerate clean safe context
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Storage access blocked or JSON parse error
  }
  return null;
}

function saveSessionToStorage(state: WebSessionState): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    // Storage access blocked
  }
}

let lastTouchActivityTime = 0;
const TOUCH_THROTTLE_MS = 10_000; // at most once every 10 seconds

export function touchActivity(): void {
  try {
    const now = Date.now();
    if (now - lastTouchActivityTime < TOUCH_THROTTLE_MS) return;
    lastTouchActivityTime = now;
    getOrInitSessionSync(true);
  } catch {
    // Best-effort
  }
}

export function getOrInitSessionSync(isUserAction = true): WebSessionState {
  const now = Date.now();
  let session = getSessionFromStorage() || inMemorySession;

  if (session) {
    const elapsed = now - session.last_user_interaction_at;
    if (elapsed < 0) {
      // Clock shifted backwards
      session.last_user_interaction_at = now;
      saveSessionToStorage(session);
      inMemorySession = session;
      return session;
    }
    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      // Expired after 30 minutes inactivity
      session = null;
    }
  }

  if (!session) {
    const rawPath = typeof window !== 'undefined' ? (window.location.pathname || '/') : '/';
    const landingPath = normalizeRoutePath(rawPath);
    const landingLocale = typeof window !== 'undefined' ? detectLocaleFromPath(rawPath) : 'tr';
    const sourceCategory = resolveSourceCategory();
    const deviceCategory = resolveDeviceCategory();

    session = {
      session_id: createId(),
      last_user_interaction_at: now,
      landing_path: landingPath,
      landing_locale: landingLocale,
      source_category: sourceCategory,
      device_category: deviceCategory,
    };
    saveSessionToStorage(session);
    inMemorySession = session;
  } else if (isUserAction) {
    session.last_user_interaction_at = now;
    saveSessionToStorage(session);
    inMemorySession = session;
  }

  return session;
}

function isPrerenderOrBot(): boolean {
  try {
    if (typeof window === 'undefined') return true;
    if (window.location.port === '5183') return true;
    if (typeof navigator !== 'undefined') {
      if (navigator.webdriver) return true;
      if (/HeadlessChrome|prerender/i.test(navigator.userAgent)) return true;
    }
  } catch {
    return true;
  }
  return false;
}

interface EventPayloadV2 {
  event_id: string;
  event_name: string;
  platform: 'web';
  client_id?: string;
  session_id: string;
  locale: Locale;
  path: string;
  app_version?: string;
  schema_version: number;
  occurred_at: string;
  device_category: DeviceCategory;
  attempt_id?: string;
  landing_path: string;
  landing_locale: string;
  source_category: SourceCategory;
  params: Record<string, string | number | boolean | null>;
}

async function sendEventV2(payload: EventPayloadV2): Promise<void> {
  try {
    if (isPrerenderOrBot()) return;

    let token: string | null = null;
    try {
      token = localStorage.getItem('sudoku_token');
    } catch {
      // Storage access blocked / private browsing security error
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const clientId = await getClientId().catch(() => createId());
    payload.client_id = clientId;

    const body = JSON.stringify({
      events: [payload],
    });

    const response = await fetch(`${API}/analytics/events`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    });

    if (response.status === 401 && token) {
      await fetch(`${API}/analytics/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    }
  } catch {
    // Analytics is deliberately best-effort and must never affect UX or reject unhandled.
  }
}

/** Record one bounded product event with synchronous context capture. */
export function track(
  eventName: string,
  params: AnalyticsParams = {},
  options?: AnalyticsTrackOptions,
): void {
  try {
    const isUserAction = options?.is_user_interaction !== false && (options as any)?.user !== false;
    const session = getOrInitSessionSync(isUserAction);
    const capturedOccurredAt = new Date().toISOString();
    const capturedEventId = createEventId();
    const capturedAttemptId = options?.attempt_id;
    const rawPath = typeof window !== 'undefined' ? (window.location.pathname || '/') : '/';
    const cleanPath = normalizeRoutePath(rawPath);
    const locale: Locale = typeof window !== 'undefined' ? detectLocaleFromPath(rawPath) : 'tr';
    const capturedParams = cleanParams(params);

    const payload: EventPayloadV2 = {
      event_id: capturedEventId,
      event_name: eventName,
      platform: 'web',
      session_id: session.session_id,
      locale,
      path: cleanPath,
      app_version: APP_VERSION,
      schema_version: 2,
      occurred_at: capturedOccurredAt,
      device_category: session.device_category,
      attempt_id: capturedAttemptId,
      landing_path: session.landing_path,
      landing_locale: session.landing_locale,
      source_category: session.source_category,
      params: capturedParams,
    };

    void sendEventV2(payload).catch(() => {});
  } catch {
    // Best-effort
  }
}

export function trackPageView(pathname: string, locale: Locale, _search = ''): void {
  try {
    if (pathname.startsWith('/admin')) return;
    const cleanPath = normalizeRoutePath(pathname);
    if (lastPageView === cleanPath) return;
    lastPageView = cleanPath;

    track('page_view', { locale }, { is_user_interaction: true });
  } catch {
    // Best-effort
  }
}

export function resetAnalyticsSession(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // Storage access blocked
  }
  inMemorySession = null;
  lastPageView = null;
}
